import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AssetsReadinessTask, ReadinessTask } from 'modules/overlay-v2/domain/interfaces/readiness';
import { PreloadImagesResult } from 'shared/utils/preload-images';

import { preloadGameInfoAssets } from './actions';

/**
 * Шаги подготовки, у которых нет своего места в остальных слайсах. Остальные пункты
 * чеклиста читаются из существующего состояния (`overlay`, `gameInfo`, `templateEditor`)
 * — дублировать их сюда нельзя, иначе появится второй источник истины.
 */
export type ReadinessTaskId = 'device' | 'chronology' | 'matchTime' | 'assets' | 'template';

export type ReadinessSliceState = {
  device: ReadinessTask;
  chronology: ReadinessTask;
  matchTime: ReadinessTask;
  assets: AssetsReadinessTask;
  template: ReadinessTask;

  /** Номер попытки подготовки: 1 — первая, дальше растёт после каждого падения. */
  attempt: number;
  /** Когда сработает автоматический повтор (epoch ms). `undefined` — повтор не запланирован. */
  retryAt?: number;
  /** Растёт на каждой попытке: эффект инициализации перезапускается по нему. */
  retryToken: number;

  /**
   * Подготовка этого матча уже сошлась хотя бы раз — табло вышло в эфир.
   *
   * Флаг однонаправленный: поднимается один раз и снимается только вместе со всеми
   * шагами, то есть при смене матча (`resetReadiness`). Пока он поднят, ни чеклист, ни знак
   * переключения не показываются, а табло не размонтируется, чем бы ни моргнули сигналы: экран
   * подготовки во весь экран поверх идущего эфира хуже любой проблемы, которую он
   * описывает. Про сбои в эфире рассказывает `OverlayConnectionIndicator`.
   */
  hasGoneLive: boolean;

  /**
   * На этой странице хоть один матч уже выходил в эфир. Отличает первое открытие (чеклист)
   * от переключения матча (знак ImGame). Живёт до перезагрузки страницы: `resetReadiness`
   * его не снимает.
   */
  hasEverGoneLive: boolean;
  /**
   * При переключении матча уже выходил чеклист — из-за ошибки или по таймауту. Дальше он
   * остаётся до готовности и не прыгает обратно на знак. Снимается сменой матча.
   */
  isChecklistEscalated: boolean;
  /** Когда началась подготовка текущего матча (epoch ms) — от неё считается таймаут знака. */
  preparationStartedAt?: number;
};

const idleTask: ReadinessTask = { status: 'idle' };

/**
 * Пауза перед следующей автоматической попыткой, по номеру попытки. Растёт, чтобы
 * при долгом обрыве связи не молотить запросами, но первая повторная попытка была быстрой:
 * чаще всего это моргнувший Wi-Fi в зале.
 */
export const RETRY_DELAYS_MS = [3_000, 6_000, 12_000, 30_000];

export function retryDelayMs(attempt: number): number {
  return RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length) - 1];
}

const initialState: ReadinessSliceState = {
  device: idleTask,
  chronology: idleTask,
  matchTime: idleTask,
  assets: idleTask,
  template: idleTask,
  attempt: 1,
  retryToken: 0,
  hasGoneLive: false,
  hasEverGoneLive: false,
  isChecklistEscalated: false,
};

export const readinessSlice = createSlice({
  name: 'overlay-v2/readiness',
  initialState,
  reducers: {
    readinessTaskStarted(state, action: PayloadAction<ReadinessTaskId>) {
      state[action.payload] = { status: 'running' };
    },
    readinessTaskSucceeded(state, action: PayloadAction<ReadinessTaskId>) {
      state[action.payload] = { status: 'done' };
    },
    readinessTaskFailed(state, action: PayloadAction<{ id: ReadinessTaskId; error?: string }>) {
      state[action.payload.id] = { status: 'failed', error: action.payload.error };
    },
    /**
     * Назначить автоматический повтор. `now` приходит снаружи, чтобы редьюсер оставался
     * чистым; обратный отсчёт до `retryAt` рисует сам чеклист.
     */
    retryScheduled(state, action: PayloadAction<{ now: number }>) {
      state.retryAt = action.payload.now + retryDelayMs(state.attempt);
    },
    /** Повтор больше не нужен — шаг починился сам (например, вернулась сеть). */
    retryCancelled(state) {
      state.retryAt = undefined;
    },
    /** Подготовка сошлась впервые — дальше чеклист этому матчу больше не показываем. */
    wentLive(state) {
      state.hasGoneLive = true;
      state.hasEverGoneLive = true;
    },
    /** Переключение матча не уложилось в тихий режим — показываем чеклист до готовности. */
    checklistEscalated(state) {
      state.isChecklistEscalated = true;
    },
    /**
     * Запустить подготовку заново — по таймеру автоповтора или по кнопке «Повторить».
     * По `retryToken` перезапускается всё, что грузится к трансляции: эффект инициализации
     * (данные матча, журнал, время, картинки), загрузка оформления и запрос подписки.
     *
     * `hasGoneLive`, `hasEverGoneLive` и `isChecklistEscalated` намеренно не трогаем: повтор — это не новый матч. Иначе кнопка
     * «Повторить» на панели вернула бы полноэкранный чеклист в идущий эфир.
     */
    retryRequested(state) {
      state.attempt += 1;
      state.retryAt = undefined;
      state.retryToken += 1;
      state.device = idleTask;
      state.chronology = idleTask;
      state.matchTime = idleTask;
      state.assets = idleTask;
      state.template = idleTask;
    },
    /**
     * Сменился матч — готовимся с нуля. `retryToken` намеренно не сбрасывается: он только
     * растёт, иначе сам сброс сдвинул бы зависимость эффекта инициализации и запустил
     * лишний круг загрузки.
     *
     * Экран подготовки нового матча — знак ImGame, если на странице уже был эфир, иначе
     * чеклист (`resolvePreparationScreen`).
     *
     * Это единственное место, где снимаются `hasGoneLive` и `isChecklistEscalated`.
     * `hasEverGoneLive` остаётся: по нему переключение отличается от первого открытия.
     * `now` приходит снаружи, как у `retryScheduled`, — от него считается таймаут знака.
     */
    resetReadiness(state, action: PayloadAction<{ now: number }>) {
      state.device = idleTask;
      state.chronology = idleTask;
      state.matchTime = idleTask;
      state.assets = idleTask;
      state.template = idleTask;
      state.attempt = 1;
      state.retryAt = undefined;
      state.hasGoneLive = false;
      state.isChecklistEscalated = false;
      state.preparationStartedAt = action.payload.now;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(preloadGameInfoAssets.pending, (state) => {
      state.assets = { status: 'running' };
    });

    builder.addCase(preloadGameInfoAssets.fulfilled, (state, action: PayloadAction<PreloadImagesResult>) => {
      state.assets = { status: 'done', report: action.payload };
    });

    builder.addCase(preloadGameInfoAssets.rejected, (state, action) => {
      state.assets = { status: 'failed', error: (action.payload as string) ?? action.error.message };
    });
  },
});

export const {
  readinessTaskStarted,
  readinessTaskSucceeded,
  readinessTaskFailed,
  retryScheduled,
  retryCancelled,
  retryRequested,
  resetReadiness,
  wentLive,
  checklistEscalated,
} = readinessSlice.actions;
